import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Save, Loader2, Archive, FileCheck2, Plus, Trash2, Eye, Code2,
  Copy, KeyRound, UserX, UserCheck, Filter, ArrowLeft, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { levelLabel, industryLabel } from "@/lib/labels";
import {
  adminListScenarios, adminCreateScenario, adminUpdateScenario,
  adminPublishScenario, adminArchiveScenario, adminStats, adminAudit, adminListIssues,
  adminDashboard, adminListUsers, adminSuspendUser, adminGenerateResetLink,
  adminBulkDeleteUsers,
} from "@/lib/api";

const EMPTY_DRAFT = () => ({
  override_of: "",
  title_es: "", title_en: "",
  category_es: "", category_en: "",
  difficulty: "basic", time_min: 5, tags: [],
  icon: "MessageSquare",
  partner: "customer",
  goal_es: "", goal_en: "",
  context_es: "", context_en: "",
  customer_persona: "",
  facts: [{ es: "", en: "" }, { es: "", en: "" }],
  checkpoints: [
    { id: "greet", skill_id: "greet_professionally", es: "", en: "" },
    { id: "ask", skill_id: "ask_missing_details", es: "", en: "" },
    { id: "close", skill_id: "close_politely", es: "", en: "" },
  ],
  phrases: [{ en: "", es: "" }, { en: "", es: "" }, { en: "", es: "" }],
  opener_en: "",
});

export default function Admin() {
  const { user } = useAuth();
  const { tr } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard"); // dashboard | users | scenarios

  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/home" replace />;

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-10 space-y-4" data-testid="admin-screen">
        <button
          type="button"
          onClick={() => navigate("/home")}
          className="wpe-ghost text-[12px]"
          data-testid="admin-back-to-app-btn"
        >
          <ArrowLeft size={14} />
          <span>{tr("adminBackToApp")}</span>
        </button>

        <h1
          className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]"
          data-testid="admin-title"
        >
          {tr("adminEntryLabel")}
        </h1>

        <div
          className="flex gap-1 rounded-full bg-[rgb(var(--wpe-subtle))] p-1"
          role="tablist"
          data-testid="admin-tabs"
        >
          <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="admin-tab-dashboard">
            {tr("adminTabDashboard")}
          </TabBtn>
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="admin-tab-users">
            {tr("adminTabUsers")}
          </TabBtn>
          <TabBtn active={tab === "scenarios"} onClick={() => setTab("scenarios")} testid="admin-tab-scenarios">
            {tr("adminTabScenarios")}
          </TabBtn>
        </div>

        {tab === "dashboard" && <DashboardTab />}
        {tab === "users" && <UsersTab />}
        {tab === "scenarios" && <ScenariosTab />}

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 rounded-full py-1.5 text-[12px] font-semibold transition-colors ${
      active
        ? "bg-white text-[rgb(var(--wpe-navy))] shadow-sm"
        : "text-[rgb(var(--wpe-muted))] hover:text-[rgb(var(--wpe-navy))]"
    }`}
    role="tab"
    aria-selected={active}
    data-testid={testid}
  >
    {children}
  </button>
);

// ---------------- Dashboard ----------------

function DashboardTab() {
  const { tr, lang } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    adminDashboard()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Failed"));
    return () => { alive = false; };
  }, []);

  if (err) return <div className="wpe-card text-[13px] text-[rgb(var(--wpe-danger))]" data-testid="admin-dashboard-error">{err}</div>;
  if (!data) return <div className="wpe-card text-[13px]" data-testid="admin-dashboard-loading"><Loader2 size={14} className="animate-spin inline" /></div>;

  const levels = data.by_level || {};
  const industries = data.by_industry || {};

  return (
    <div className="space-y-3" data-testid="admin-dashboard">
      {!data.email_delivery_configured && (
        <div
          className="wpe-card text-[12px] text-[rgb(var(--wpe-slate))] border-l-4 border-l-amber-500"
          data-testid="admin-email-warning"
        >
          {tr("adminEmailNotConfigured")}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Metric label={tr("adminUsersTotal")} value={data.users_total} testid="metric-users-total" />
        <Metric
          label={`${tr("adminUsersActive")} (${data.users_active_window_days}d)`}
          value={data.users_active_recent}
          testid="metric-users-active"
        />
        <Metric label={tr("adminCompletedTotal")} value={data.completed_total} testid="metric-completed-total" />
        <Metric label={tr("adminCompletedToday")} value={data.completed_today} testid="metric-completed-today" />
        <Metric label={tr("adminPracticing")} value={data.practicing_users} testid="metric-practicing-users" />
        <Metric label={tr("adminAvgPerUser")} value={data.avg_completed_per_practicing_user} testid="metric-avg-per-user" />
      </div>

      <div className="wpe-card space-y-2" data-testid="admin-by-level">
        <h3 className="wpe-eyebrow">{tr("adminByLevel")}</h3>
        <div className="grid grid-cols-3 gap-2 text-center text-[12px]">
          <Cell v={levels.basic ?? 0} l={levelLabel(lang, "basic")} testid="by-level-basic" />
          <Cell v={levels.intermediate ?? 0} l={levelLabel(lang, "intermediate")} testid="by-level-intermediate" />
          <Cell v={levels.advanced ?? 0} l={levelLabel(lang, "advanced")} testid="by-level-advanced" />
        </div>
      </div>

      <div className="wpe-card space-y-2" data-testid="admin-by-industry">
        <h3 className="wpe-eyebrow">{tr("adminByIndustry")}</h3>
        {Object.keys(industries).length === 0 ? (
          <div className="text-[12px] text-[rgb(var(--wpe-muted))]">—</div>
        ) : (
          <ul className="space-y-1 text-[12px]">
            {Object.entries(industries)
              .sort((a, b) => b[1] - a[1])
              .map(([ind, n]) => (
                <li key={ind} className="flex justify-between" data-testid={`by-industry-${ind}`}>
                  <span className="text-[rgb(var(--wpe-slate))]">{industryLabel(lang, ind)}</span>
                  <span className="font-semibold text-[rgb(var(--wpe-navy))]">{n}</span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Metric = ({ label, value, testid }) => (
  <div className="wpe-card text-center py-2" data-testid={testid}>
    <div className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-[rgb(var(--wpe-muted))]">{label}</div>
  </div>
);

const Cell = ({ v, l, testid }) => (
  <div className="rounded-lg bg-[rgb(var(--wpe-subtle))] py-2" data-testid={testid}>
    <div className="text-[18px] font-bold text-[rgb(var(--wpe-navy))]">{v}</div>
    <div className="text-[10px] text-[rgb(var(--wpe-muted))]">{l}</div>
  </div>
);

// ---------------- Users ----------------

function UsersTab() {
  const { tr, lang } = useLang();
  const { user: adminUser } = useAuth();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // user id currently mutating
  const [resetLink, setResetLink] = useState(null); // {email, link}
  const [viewing, setViewing] = useState(null);
  // Admin V1 bulk-delete state. `selected` holds only user IDs that are
  // in the CURRENT filtered result set — Select All never expands beyond
  // it, per product spec.
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmType, setConfirmType] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async (query) => {
    setLoading(true);
    try {
      const data = await adminListUsers(query || undefined);
      setRows(data);
      // Any selected IDs no longer in the result set are dropped so
      // stale selections cannot silently leak into bulk delete.
      const rowIds = new Set(data.map((r) => r.id));
      setSelected((prev) => new Set([...prev].filter((id) => rowIds.has(id))));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  // Only rows OTHER than the currently-signed-in admin are selectable/deletable.
  const selectableRows = useMemo(
    () => rows.filter((r) => r.id !== adminUser?.id),
    [rows, adminUser?.id]
  );
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));

  const toggleRow = (id) => {
    if (id === adminUser?.id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllCurrent = () => {
    setSelected(new Set(selectableRows.map((r) => r.id)));
  };

  const clearSelection = () => setSelected(new Set());

  const openConfirm = () => {
    if (selected.size === 0) return;
    setConfirmType("");
    setConfirmDelete(true);
  };

  const runBulkDelete = async () => {
    if (confirmType !== "DELETE" || selected.size === 0) return;
    setDeleting(true);
    try {
      const ids = [...selected];
      const data = await adminBulkDeleteUsers(ids, "DELETE");
      const n = data?.deleted?.users ?? 0;
      toast.success(tr("adminBulkDeletedToast").replace("{n}", n));
      setConfirmDelete(false);
      setSelected(new Set());
      await load(q.trim());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSuspend = async (u) => {
    const next = !u.suspended;
    const msg = next ? tr("adminConfirmSuspend") : tr("adminConfirmReactivate");
    if (!window.confirm(msg)) return;
    setBusy(u.id);
    try {
      await adminSuspendUser(u.id, next);
      setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, suspended: next } : r)));
      toast.success("OK");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async (u) => {
    setBusy(u.id);
    try {
      const data = await adminGenerateResetLink(u.id);
      setResetLink({ email: u.email, link: data.reset_link, expires_at: data.expires_at });
      toast.success(tr("adminResetLinkGenerated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    if (!resetLink?.link) return;
    try {
      await navigator.clipboard.writeText(resetLink.link);
      toast.success(tr("copied"));
    } catch (_) { /* ignored */ }
  };

  return (
    <div className="space-y-3" data-testid="admin-users">
      <form onSubmit={onSearch} className="wpe-card flex gap-2" data-testid="admin-users-search-form">
        <input
          className="wpe-input flex-1 text-[13px]"
          placeholder={tr("adminSearchUsers")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-users-search-input"
        />
        <button type="submit" className="wpe-secondary text-[12px]" data-testid="admin-users-search-btn">
          <Filter size={12} />
        </button>
      </form>

      {/* Bulk-select actions bar. Select-all applies to the CURRENT
          filtered/search results only (per product spec). */}
      {selectableRows.length > 0 && (
        <div
          className="wpe-card flex items-center justify-between gap-2 flex-wrap"
          data-testid="admin-users-bulk-bar"
        >
          <div className="flex items-center gap-2 text-[12px] text-[rgb(var(--wpe-navy))]">
            <button
              type="button"
              onClick={allSelected ? clearSelection : selectAllCurrent}
              className="wpe-ghost text-[11px]"
              data-testid="admin-users-select-all-btn"
            >
              {allSelected ? tr("adminBulkClear") : tr("adminBulkSelectAll")}
            </button>
            <span
              className="text-[11px] text-[rgb(var(--wpe-muted))]"
              data-testid="admin-users-selected-count"
            >
              {tr("adminBulkSelectedCount").replace("{n}", selected.size)}
            </span>
          </div>
          <button
            type="button"
            onClick={openConfirm}
            disabled={selected.size === 0}
            className={`text-[11px] px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1 ${
              selected.size === 0
                ? "bg-[rgb(var(--wpe-border))] text-[rgb(var(--wpe-muted))]"
                : "bg-[rgb(var(--wpe-danger))] text-white"
            }`}
            data-testid="admin-users-bulk-delete-btn"
          >
            <Trash2 size={12} /> {tr("adminBulkDeleteBtn")}
          </button>
        </div>
      )}

      {resetLink && (
        <div className="wpe-card space-y-2" data-testid="admin-reset-link-card">
          <div className="text-[12px] font-semibold text-[rgb(var(--wpe-navy))]">
            {resetLink.email}
          </div>
          <div className="rounded-md bg-[rgb(var(--wpe-subtle))] p-2 text-[11px] break-all font-mono text-[rgb(var(--wpe-navy))]" data-testid="admin-reset-link-value">
            {resetLink.link}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={copyLink} className="wpe-secondary text-[12px]" data-testid="admin-reset-link-copy-btn">
              <Copy size={12} /> {tr("copyResetLink")}
            </button>
            <button type="button" onClick={() => setResetLink(null)} className="wpe-ghost text-[12px]" data-testid="admin-reset-link-dismiss-btn">
              ✕
            </button>
          </div>
        </div>
      )}

      {viewing && (
        <div className="wpe-card space-y-1 text-[12px]" data-testid="admin-user-profile">
          <div><span className="wpe-eyebrow">{tr("adminColEmail")}</span> {viewing.email}</div>
          <div><span className="wpe-eyebrow">{tr("adminColLevel")}</span> {viewing.level ? levelLabel(lang, viewing.level) : "—"}</div>
          <div><span className="wpe-eyebrow">{tr("adminColIndustry")}</span> {viewing.industry ? industryLabel(lang, viewing.industry) : "—"}</div>
          <div><span className="wpe-eyebrow">{tr("adminColUiLang")}</span> {viewing.ui_lang}</div>
          <div><span className="wpe-eyebrow">{tr("adminColJoined")}</span> {fmt(viewing.joined_at)}</div>
          <div><span className="wpe-eyebrow">{tr("adminColLastActive")}</span> {fmt(viewing.last_active)}</div>
          <div><span className="wpe-eyebrow">{tr("adminColStatus")}</span> {viewing.suspended ? tr("adminStatusSuspended") : tr("adminStatusActive")}</div>
          <button type="button" onClick={() => setViewing(null)} className="wpe-ghost text-[11px]" data-testid="admin-user-profile-close">✕</button>
        </div>
      )}

      <div className="wpe-card p-0 overflow-hidden" data-testid="admin-users-table">
        {loading && (
          <div className="p-3 text-[12px]" data-testid="admin-users-loading">
            <Loader2 size={14} className="inline animate-spin" />
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-4 text-[12px] text-[rgb(var(--wpe-muted))]" data-testid="admin-users-empty">
            {tr("adminNoUsers")}
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-[rgb(var(--wpe-border))]">
            {rows.map((u) => (
              <li key={u.id} className="p-3 space-y-1 text-[12px]" data-testid={`admin-user-row-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  {u.id === adminUser?.id ? (
                    <span
                      className="mt-1 h-4 w-4 shrink-0 rounded border border-[rgb(var(--wpe-border))] bg-[rgb(var(--wpe-subtle))] inline-block"
                      title={tr("adminBulkSelfExcludedNote")}
                      data-testid={`admin-user-checkbox-self-${u.id}`}
                    />
                  ) : (
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[rgb(var(--wpe-teal))]"
                      checked={selected.has(u.id)}
                      onChange={() => toggleRow(u.id)}
                      aria-label={u.email}
                      data-testid={`admin-user-checkbox-${u.id}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[rgb(var(--wpe-navy))] break-all" data-testid={`admin-user-email-${u.id}`}>
                      {u.email}
                    </div>
                    <div className="text-[10px] text-[rgb(var(--wpe-muted))]">
                      {(u.level ? levelLabel(lang, u.level) : "—")} · {(u.industry ? industryLabel(lang, u.industry) : "—")} · {u.ui_lang} · {fmt(u.joined_at)}
                    </div>
                    <div className="text-[10px] text-[rgb(var(--wpe-muted))]">
                      {tr("adminColLastActive")}: {fmt(u.last_active)}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      u.suspended
                        ? "bg-[rgb(var(--wpe-danger)_/_0.12)] text-[rgb(var(--wpe-danger))]"
                        : "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-navy))]"
                    }`}
                    data-testid={`admin-user-status-${u.id}`}
                  >
                    {u.suspended ? tr("adminStatusSuspended") : tr("adminStatusActive")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 pt-1">
                  <button
                    type="button"
                    onClick={() => setViewing(u)}
                    className="wpe-ghost text-[11px]"
                    data-testid={`admin-user-view-${u.id}`}
                  >
                    <Eye size={11} /> {tr("adminActionView")}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleSuspend(u)}
                    disabled={busy === u.id}
                    className="wpe-ghost text-[11px]"
                    data-testid={`admin-user-suspend-${u.id}`}
                  >
                    {u.suspended ? <UserCheck size={11} /> : <UserX size={11} />}{" "}
                    {u.suspended ? tr("adminActionReactivate") : tr("adminActionSuspend")}
                  </button>
                  <button
                    type="button"
                    onClick={() => resetPassword(u)}
                    disabled={busy === u.id}
                    className="wpe-ghost text-[11px]"
                    data-testid={`admin-user-reset-${u.id}`}
                  >
                    <KeyRound size={11} /> {tr("adminActionResetPassword")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bulk-delete confirmation modal — requires typing DELETE. */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-[rgb(0_0_0_/_0.45)] px-4"
          role="dialog"
          aria-modal="true"
          data-testid="admin-users-bulk-confirm-modal"
        >
          <div className="wpe-card max-w-[380px] w-full space-y-3 relative">
            <button
              type="button"
              onClick={() => (deleting ? null : setConfirmDelete(false))}
              className="absolute right-2 top-2 text-[rgb(var(--wpe-muted))]"
              data-testid="admin-users-bulk-confirm-close"
              aria-label="Close"
            >
              <X size={14} />
            </button>
            <div className="flex items-start gap-2">
              <span className="w-8 h-8 rounded-full bg-[rgb(var(--wpe-danger)_/_0.12)] text-[rgb(var(--wpe-danger))] flex items-center justify-center shrink-0">
                <AlertTriangle size={16} />
              </span>
              <h3
                className="text-[14px] font-bold text-[rgb(var(--wpe-navy))]"
                data-testid="admin-users-bulk-confirm-title"
              >
                {tr("adminBulkConfirmTitle").replace("{n}", selected.size)}
              </h3>
            </div>
            <p className="text-[12px] text-[rgb(var(--wpe-slate))]" data-testid="admin-users-bulk-confirm-body">
              {tr("adminBulkConfirmBody")}
            </p>
            <label
              className="block text-[11px] text-[rgb(var(--wpe-navy))] font-semibold"
              htmlFor="admin-bulk-confirm-input"
            >
              {tr("adminBulkConfirmTypeLabel")}
            </label>
            <input
              id="admin-bulk-confirm-input"
              autoFocus
              className="wpe-input w-full text-[13px]"
              value={confirmType}
              onChange={(e) => setConfirmType(e.target.value)}
              placeholder="DELETE"
              data-testid="admin-users-bulk-confirm-input"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="wpe-ghost text-[12px]"
                data-testid="admin-users-bulk-confirm-cancel"
              >
                {tr("adminBulkConfirmCancel")}
              </button>
              <button
                type="button"
                onClick={runBulkDelete}
                disabled={confirmType !== "DELETE" || deleting}
                className={`text-[12px] px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1 ${
                  confirmType !== "DELETE" || deleting
                    ? "bg-[rgb(var(--wpe-border))] text-[rgb(var(--wpe-muted))]"
                    : "bg-[rgb(var(--wpe-danger))] text-white"
                }`}
                data-testid="admin-users-bulk-confirm-confirm"
              >
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                {tr("adminBulkConfirmConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  } catch (_) {
    return "—";
  }
}

// ---------------- Scenarios ----------------

const INDUSTRY_OPTIONS = [
  "restaurant", "hospitality", "travel", "retail", "call_center",
  "hospital_cleaning", "office",
  "warehouse_logistics", "healthcare_support", "construction_trades", "landscaping",
];
const LEVEL_OPTIONS = ["basic", "intermediate", "advanced"];

function ScenariosTab() {
  const { tr, lang } = useLang();
  const [data, setData] = useState({ drafts_and_overrides: [], curated: [], allowed_skill_ids: [] });
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState([]);
  const [issues, setIssues] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT());
  const [busy, setBusy] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [preview, setPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [showEditor, setShowEditor] = useState(false);

  const reload = async () => {
    try {
      const [sc, st, au, iss] = await Promise.all([
        adminListScenarios(), adminStats(), adminAudit(), adminListIssues(),
      ]);
      setData(sc); setStats(st); setAudit(au); setIssues(iss);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Admin data failed");
    }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const jsonView = useMemo(() => JSON.stringify(draft, null, 2), [draft]);

  const curatedFiltered = useMemo(() => {
    return (data.curated || []).filter((c) => {
      if (filterLevel && c.difficulty !== filterLevel) return false;
      if (filterIndustry) {
        const tags = c.tags || [];
        if (!tags.includes(filterIndustry)) return false;
      }
      return true;
    });
  }, [data.curated, filterIndustry, filterLevel]);

  const validate = () => {
    const e = {};
    if (!draft.title_es || !draft.title_en) e.title = "Both EN and ES titles are required";
    if (!draft.goal_es || !draft.goal_en) e.goal = "Both goals are required";
    if (!draft.context_es || !draft.context_en) e.context = "Both contexts are required";
    if ((draft.facts || []).filter((f) => f.es && f.en).length < 2) e.facts = "At least 2 facts (EN + ES) are required";
    if ((draft.checkpoints || []).filter((c) => c.id && c.es && c.en).length < 3) e.checkpoints = "At least 3 checkpoints are required";
    if ((draft.checkpoints || []).some((c) => c.skill_id && !data.allowed_skill_ids.includes(c.skill_id))) e.skillIds = "Some skill IDs are not in the allow-list";
    if ((draft.phrases || []).filter((p) => p.en).length < 3) e.phrases = "At least 3 phrases are required";
    if (!["basic","intermediate","advanced"].includes(draft.difficulty)) e.difficulty = "Invalid difficulty";
    if (![5,10,15].includes(Number(draft.time_min))) e.time_min = "Time must be 5, 10 or 15";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = async () => {
    if (!validate()) { toast.error("Please fix highlighted fields"); return; }
    setBusy(true);
    try {
      const payload = { ...draft, time_min: Number(draft.time_min), override_of: draft.override_of || null, tags: Array.isArray(draft.tags) ? draft.tags : String(draft.tags || "").split(",").map((x) => x.trim()).filter(Boolean) };
      if (selectedId) await adminUpdateScenario(selectedId, payload);
      else {
        const created = await adminCreateScenario(payload);
        setSelectedId(created.id);
      }
      await reload();
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const doPublish = async () => {
    if (!selectedId) return;
    if (!window.confirm("Publish this draft to the in-app catalog?")) return;
    setBusy(true);
    try { await adminPublishScenario(selectedId); await reload(); toast.success("Published"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doArchive = async () => {
    if (!selectedId) return;
    if (!window.confirm("Archive this scenario? Historical sessions keep their frozen snapshot.")) return;
    setBusy(true);
    try { await adminArchiveScenario(selectedId); await reload(); toast.success("Archived"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const newDraft = () => { setSelectedId(null); setDraft(EMPTY_DRAFT()); setErrors({}); setShowEditor(true); };
  const loadCurated = (id) => {
    const c = (data.curated || []).find((x) => x.id === id);
    if (!c) return;
    setSelectedId(null);
    setDraft({ ...EMPTY_DRAFT(), ...c, override_of: id, tags: c.tags || [], phrases: c.phrases || [], facts: c.facts || [], checkpoints: c.checkpoints || [] });
    setErrors({});
    setShowEditor(true);
    toast.info(`Prefilled from ${id}. Save creates a new draft override.`);
  };
  const openOverride = (row) => {
    setSelectedId(row.id);
    setDraft({ override_of: row.override_of || "", ...EMPTY_DRAFT(), ...row.scenario, tags: row.scenario?.tags || [] });
    setErrors({});
    setShowEditor(true);
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setListItem = (list, i, key, v) => setDraft((d) => ({ ...d, [list]: d[list].map((r, idx) => idx === i ? { ...r, [key]: v } : r) }));
  const addRow = (list, row) => setDraft((d) => ({ ...d, [list]: [...(d[list] || []), row] }));
  const rmRow = (list, i) => setDraft((d) => ({ ...d, [list]: d[list].filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-3" data-testid="admin-scenarios">
      {stats && (
        <div className="wpe-card grid grid-cols-3 gap-2 text-center text-[11px]" data-testid="admin-stats">
          <Cell v={stats.users_total} l="users" testid="stat-users-total" />
          <Cell v={stats.saved_sessions} l="saved sess." testid="stat-saved" />
          <Cell v={stats.completed_sessions} l="completed" testid="stat-completed" />
          <Cell v={stats.custom_scenarios} l="custom" testid="stat-custom" />
          <Cell v={stats.phrases_total} l="phrases" testid="stat-phrases" />
          <Cell v={stats.issues_open} l="open issues" testid="stat-issues" />
        </div>
      )}

      <div className="wpe-card space-y-3" data-testid="admin-scenarios-filters">
        <div className="flex items-center justify-between">
          <h2 className="wpe-eyebrow">Curated seed catalog ({(data.curated || []).length})</h2>
          <button type="button" onClick={newDraft} className="wpe-ghost text-[11px]" data-testid="admin-new-draft-btn">
            <Plus size={12}/> New draft
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[12px]">
          <label>
            <span className="wpe-eyebrow">{tr("adminFilterIndustry")}</span>
            <select
              className="wpe-input text-[12px]"
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              data-testid="admin-filter-industry"
            >
              <option value="">{tr("adminFilterAll")}</option>
              {INDUSTRY_OPTIONS.map((ind) => (
                <option key={ind} value={ind}>{industryLabel(lang, ind)}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="wpe-eyebrow">{tr("adminFilterLevel")}</span>
            <select
              className="wpe-input text-[12px]"
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              data-testid="admin-filter-level"
            >
              <option value="">{tr("adminFilterAll")}</option>
              {LEVEL_OPTIONS.map((l) => (
                <option key={l} value={l}>{levelLabel(lang, l)}</option>
              ))}
            </select>
          </label>
        </div>

        <div
          className="max-h-[240px] overflow-y-auto space-y-1"
          data-testid="admin-scenarios-list"
        >
          {curatedFiltered.length === 0 && (
            <div className="text-[12px] text-[rgb(var(--wpe-muted))] px-2 py-1" data-testid="admin-scenarios-empty">—</div>
          )}
          {curatedFiltered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => loadCurated(c.id)}
              className="w-full text-left text-[12px] px-2 py-1 rounded hover:bg-[rgb(var(--wpe-subtle))]"
              data-testid={`admin-curated-${c.id}`}
            >
              <span className="uppercase text-[9px] font-semibold text-[rgb(var(--wpe-muted))]">{levelLabel(lang, c.difficulty)}</span>{" · "}
              <span className="text-[rgb(var(--wpe-navy))] font-medium">{lang === "es" ? (c.title_es || c.title_en) : c.title_en}</span>
              <span className="text-[10px] text-[rgb(var(--wpe-muted))]"> ({c.id})</span>
              <span className="text-[9px] text-[rgb(var(--wpe-teal))] ml-1" data-testid={`admin-scenario-tags-${c.id}`}>
                {(c.tags || []).filter((t) => INDUSTRY_OPTIONS.includes(t)).map((t) => industryLabel(lang, t)).join(", ")}
              </span>
              <span className="text-[9px] ml-1 uppercase font-semibold text-[rgb(var(--wpe-navy))]">
                {tr("adminScenarioActive")}
              </span>
            </button>
          ))}
        </div>

        <h2 className="wpe-eyebrow mt-2">Your drafts / overrides</h2>
        <ul className="space-y-1 max-h-[160px] overflow-y-auto" data-testid="admin-drafts-list">
          {(data.drafts_and_overrides || []).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => openOverride(s)}
                className={`w-full text-left text-[12px] px-2 py-1 rounded ${
                  selectedId === s.id ? "bg-[rgb(var(--wpe-teal-subtle))]" : "hover:bg-[rgb(var(--wpe-subtle))]"
                }`}
                data-testid={`admin-draft-${s.id}`}
              >
                <span className="uppercase text-[9px] font-semibold text-[rgb(var(--wpe-muted))]">{s.state}</span>{" · "}
                <span className="text-[rgb(var(--wpe-navy))] font-medium">{s.scenario?.title_en || "(untitled)"}</span>
                {s.override_of && <span className="text-[10px] text-[rgb(var(--wpe-muted))]"> (overrides {s.override_of})</span>}
              </button>
            </li>
          ))}
          {(!data.drafts_and_overrides || data.drafts_and_overrides.length === 0) && (
            <li className="text-[11px] text-[rgb(var(--wpe-muted))]">— None yet</li>
          )}
        </ul>
      </div>

      {showEditor && (
        <div className="wpe-card space-y-3" data-testid="admin-editor-card">
          <div className="flex items-center justify-between">
            <h2 className="wpe-eyebrow">Editor</h2>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPreview((v) => !v)} className="wpe-ghost text-[11px]" data-testid="admin-preview-toggle">
                <Eye size={12}/> {preview ? "Edit" : "Preview"}
              </button>
              <button type="button" onClick={() => setShowJson((v) => !v)} className="wpe-ghost text-[11px]" data-testid="admin-json-toggle">
                <Code2 size={12}/> {showJson ? "Form" : "Advanced JSON"}
              </button>
              <button type="button" onClick={() => setShowEditor(false)} className="wpe-ghost text-[11px]" data-testid="admin-editor-close">
                ✕
              </button>
            </div>
          </div>

          {preview ? (
            <div className="space-y-2 text-[13px]" data-testid="admin-preview">
              <div className="text-[10px] uppercase font-semibold text-[rgb(var(--wpe-muted))]">Learner-style preview</div>
              <div className="font-bold text-[rgb(var(--wpe-navy))]">{draft.title_es} · {draft.title_en}</div>
              <div className="text-[rgb(var(--wpe-slate))] italic">{draft.goal_es}</div>
              <div className="text-[rgb(var(--wpe-slate))]">{draft.context_es}</div>
              <ul className="list-disc pl-4 text-[12px]">
                {draft.facts.filter((f) => f.es).map((f, i) => (<li key={i}>{f.es}</li>))}
              </ul>
              <ol className="list-decimal pl-4 text-[12px] text-[rgb(var(--wpe-navy))]">
                {draft.checkpoints.filter((c) => c.es).map((c, i) => (<li key={i}>{c.es} <span className="text-[10px] text-[rgb(var(--wpe-muted))]">({c.skill_id})</span></li>))}
              </ol>
            </div>
          ) : showJson ? (
            <textarea className="wpe-input text-[11px] font-mono min-h-[280px]" value={jsonView} onChange={(e) => { try { setDraft(JSON.parse(e.target.value)); } catch (_) {} }} data-testid="admin-editor-json" />
          ) : (
            <div className="space-y-3">
              <Bilingual name="title" tr="Title" testid="title" errors={errors} es={draft.title_es} en={draft.title_en} onEs={(v) => setField("title_es", v)} onEn={(v) => setField("title_en", v)} />
              <Bilingual name="category" tr="Category" testid="category" errors={errors} es={draft.category_es} en={draft.category_en} onEs={(v) => setField("category_es", v)} onEn={(v) => setField("category_en", v)} />
              <Bilingual name="goal" tr="Goal" testid="goal" errors={errors} textarea es={draft.goal_es} en={draft.goal_en} onEs={(v) => setField("goal_es", v)} onEn={(v) => setField("goal_en", v)} />
              <Bilingual name="context" tr="Context" testid="context" errors={errors} textarea es={draft.context_es} en={draft.context_en} onEs={(v) => setField("context_es", v)} onEn={(v) => setField("context_en", v)} />
              <label className="block">
                <span className="wpe-eyebrow">Learner/partner persona (partner)</span>
                <select className="wpe-input text-[13px]" value={draft.partner} onChange={(e) => setField("partner", e.target.value)} data-testid="admin-partner">
                  <option value="customer">customer</option><option value="coworker">coworker</option><option value="supervisor">supervisor</option><option value="patient_visitor">patient_visitor</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label>
                  <span className="wpe-eyebrow">Difficulty</span>
                  <select className="wpe-input text-[13px]" value={draft.difficulty} onChange={(e) => setField("difficulty", e.target.value)} data-testid="admin-difficulty">
                    <option value="basic">basic</option><option value="intermediate">intermediate</option><option value="advanced">advanced</option>
                  </select>
                </label>
                <label>
                  <span className="wpe-eyebrow">Time (min)</span>
                  <select className="wpe-input text-[13px]" value={draft.time_min} onChange={(e) => setField("time_min", Number(e.target.value))} data-testid="admin-time">
                    <option value={5}>5</option><option value={10}>10</option><option value={15}>15</option>
                  </select>
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between"><span className="wpe-eyebrow">Fictional facts</span>
                  <button type="button" className="wpe-ghost text-[11px]" onClick={() => addRow("facts", { es: "", en: "" })} data-testid="admin-add-fact"><Plus size={10}/> Add fact</button>
                </div>
                {errors.facts && <div className="text-[11px] text-[rgb(var(--wpe-danger))]">{errors.facts}</div>}
                {draft.facts.map((f, i) => (
                  <div key={i} className="grid grid-cols-2 gap-1 mb-1" data-testid={`admin-fact-${i}`}>
                    <input className="wpe-input text-[12px]" placeholder="ES" value={f.es} onChange={(e) => setListItem("facts", i, "es", e.target.value)} />
                    <div className="flex gap-1"><input className="wpe-input text-[12px] flex-1" placeholder="EN" value={f.en} onChange={(e) => setListItem("facts", i, "en", e.target.value)} /><button type="button" className="wpe-ghost text-[10px]" onClick={() => rmRow("facts", i)}><Trash2 size={10}/></button></div>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between"><span className="wpe-eyebrow">Checkpoints (with skill ID)</span>
                  <button type="button" className="wpe-ghost text-[11px]" onClick={() => addRow("checkpoints", { id: `cp${draft.checkpoints.length+1}`, skill_id: "listen_actively", es: "", en: "" })} data-testid="admin-add-checkpoint"><Plus size={10}/> Add</button>
                </div>
                {errors.checkpoints && <div className="text-[11px] text-[rgb(var(--wpe-danger))]">{errors.checkpoints}</div>}
                {errors.skillIds && <div className="text-[11px] text-[rgb(var(--wpe-danger))]">{errors.skillIds}</div>}
                {draft.checkpoints.map((c, i) => (
                  <div key={i} className="space-y-1 mb-1 border-b border-[rgb(var(--wpe-border))] pb-1" data-testid={`admin-cp-${i}`}>
                    <div className="grid grid-cols-2 gap-1">
                      <input className="wpe-input text-[11px]" placeholder="id" value={c.id} onChange={(e) => setListItem("checkpoints", i, "id", e.target.value)} />
                      <select className="wpe-input text-[11px]" value={c.skill_id || ""} onChange={(e) => setListItem("checkpoints", i, "skill_id", e.target.value)}>
                        <option value="">(skill_id)</option>
                        {data.allowed_skill_ids.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <input className="wpe-input text-[12px]" placeholder="ES" value={c.es} onChange={(e) => setListItem("checkpoints", i, "es", e.target.value)} />
                      <div className="flex gap-1"><input className="wpe-input text-[12px] flex-1" placeholder="EN" value={c.en} onChange={(e) => setListItem("checkpoints", i, "en", e.target.value)} /><button type="button" className="wpe-ghost text-[10px]" onClick={() => rmRow("checkpoints", i)}><Trash2 size={10}/></button></div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between"><span className="wpe-eyebrow">Useful phrases</span>
                  <button type="button" className="wpe-ghost text-[11px]" onClick={() => addRow("phrases", { en: "", es: "" })} data-testid="admin-add-phrase"><Plus size={10}/> Add</button>
                </div>
                {errors.phrases && <div className="text-[11px] text-[rgb(var(--wpe-danger))]">{errors.phrases}</div>}
                {draft.phrases.map((p, i) => (
                  <div key={i} className="grid grid-cols-2 gap-1 mb-1" data-testid={`admin-phrase-${i}`}>
                    <input className="wpe-input text-[12px]" placeholder="EN" value={p.en} onChange={(e) => setListItem("phrases", i, "en", e.target.value)} />
                    <div className="flex gap-1"><input className="wpe-input text-[12px] flex-1" placeholder="ES" value={p.es} onChange={(e) => setListItem("phrases", i, "es", e.target.value)} /><button type="button" className="wpe-ghost text-[10px]" onClick={() => rmRow("phrases", i)}><Trash2 size={10}/></button></div>
                  </div>
                ))}
              </div>

              <label className="block">
                <span className="wpe-eyebrow">Opener (English)</span>
                <input className="wpe-input text-[12px]" value={draft.opener_en} onChange={(e) => setField("opener_en", e.target.value)} data-testid="admin-opener" />
              </label>
              <label className="block">
                <span className="wpe-eyebrow">Overrides curated ID (optional)</span>
                <input className="wpe-input text-[12px]" value={draft.override_of || ""} onChange={(e) => setField("override_of", e.target.value)} placeholder="e.g. hotel_checkin" data-testid="admin-override-of" />
              </label>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={doSave} disabled={busy} className="wpe-cta text-[12px]" data-testid="admin-save-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft
            </button>
            <button type="button" onClick={doPublish} disabled={!selectedId || busy} className="wpe-secondary text-[12px]" data-testid="admin-publish-btn">
              <FileCheck2 size={12} /> Publish
            </button>
            <button type="button" onClick={doArchive} disabled={!selectedId || busy} className="wpe-secondary text-[12px]" data-testid="admin-archive-btn">
              <Archive size={12} /> Archive
            </button>
          </div>
          <p className="text-[10px] text-[rgb(var(--wpe-muted))]">
            Drafts do not change live content until published. Historical practices keep their frozen snapshot.
          </p>
        </div>
      )}

      <div className="wpe-card space-y-2" data-testid="admin-audit">
        <h2 className="wpe-eyebrow">Audit trail</h2>
        <ul className="space-y-1 max-h-[220px] overflow-y-auto">
          {audit.slice(0, 20).map((a) => (
            <li key={a.id} className="text-[11px] text-[rgb(var(--wpe-slate))]">
              <span className="text-[rgb(var(--wpe-muted))]">{new Date(a.created_at).toLocaleString()}</span>{" · "}
              <span className="font-semibold">{a.action}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="wpe-card space-y-2" data-testid="admin-issues">
        <h2 className="wpe-eyebrow">Issue queue</h2>
        {issues.length === 0 && <div className="text-[12px] text-[rgb(var(--wpe-muted))]">—</div>}
        {issues.slice(0, 10).map((i) => (
          <div key={i.id} className="text-[12px] text-[rgb(var(--wpe-slate))] border-b last:border-b-0 border-[rgb(var(--wpe-border))] py-1">
            <div className="text-[10px] text-[rgb(var(--wpe-muted))]">{new Date(i.created_at).toLocaleString()} · {i.context || "—"}</div>
            <div className="text-[rgb(var(--wpe-navy))]">{i.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Bilingual = ({ tr, name, testid, errors, es, en, onEs, onEn, textarea }) => {
  const Input = textarea ? "textarea" : "input";
  const err = errors[name] || errors[testid];
  return (
    <div>
      <span className="wpe-eyebrow">{tr}</span>
      <div className="grid grid-cols-2 gap-2">
        <Input className={`wpe-input text-[13px] ${textarea ? "min-h-[64px]" : ""}`} placeholder="Español" value={es} onChange={(e) => onEs(e.target.value)} data-testid={`admin-${testid}-es`} />
        <Input className={`wpe-input text-[13px] ${textarea ? "min-h-[64px]" : ""}`} placeholder="English" value={en} onChange={(e) => onEn(e.target.value)} data-testid={`admin-${testid}-en`} />
      </div>
      {err && <div className="text-[11px] text-[rgb(var(--wpe-danger))]" data-testid={`admin-${testid}-err`}>{err}</div>}
    </div>
  );
};
